/**
 * Normalize editor state / AI JSON for POST /api/v1/daywise-itinerary/create|update.
 * Uses daywise schema (days, hotels.roomConfigs, transports, services) — not legacy daysRequests.
 */

const stripIds = (obj) => {
  if (obj == null || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(stripIds);
  const { id, ...rest } = obj;
  return Object.fromEntries(
    Object.entries(rest).map(([k, v]) => [k, stripIds(v)])
  );
};

const isEnabledFlag = (item) => {
  if (!item || typeof item !== 'object') return true;
  if (item.enabled === false || item.isEnabled === false) return false;
  return true;
};

const mapServiceForApi = (svc) => {
  if (!svc || typeof svc !== 'object') return svc;
  const { enabled, sightSeeingId, sightseeingCatalogueId, isEnabled, ...rest } = svc;
  return { ...rest, isEnabled: isEnabledFlag(svc) };
};

const mapTransportForApi = (t) => {
  if (!t || typeof t !== 'object') return t;
  const { enabled, isEnabled, ...rest } = t;
  return {
    ...rest,
    isEnabled: isEnabledFlag(t),
    context: t.context === 'package' ? 'full_tour' : t.context,
  };
};

const mapServiceFromApi = (svc) => {
  if (!svc || typeof svc !== 'object') return svc;
  const { isEnabled, ...rest } = svc;
  return { ...rest, enabled: isEnabledFlag(svc) };
};

const mapTransportFromApi = (t) => {
  if (!t || typeof t !== 'object') return t;
  const { isEnabled, ...rest } = t;
  return {
    ...rest,
    enabled: isEnabledFlag(t),
    context: t.context === 'full_tour' ? 'package' : t.context,
  };
};

/** Map GET /daywise-itinerary/{id|get?holidayId=} → editor state. */
export function mapItineraryApiToEditor(itinerary) {
  if (!itinerary || typeof itinerary !== 'object') return null;
  const holidayId =
    itinerary.holiday?.id ?? itinerary.holidayId ?? '';
  return {
    id: itinerary.id,
    holidayId: holidayId === '' ? '' : String(holidayId),
    itineraryName: itinerary.itineraryName || '',
    route: itinerary.route || '',
    totalDays: Number(itinerary.totalDays) || (itinerary.days || []).length || 1,
    totalNights:
      itinerary.totalNights != null
        ? Number(itinerary.totalNights)
        : Math.max(0, (Number(itinerary.totalDays) || 1) - 1),
    markupPercent: Number(itinerary.markupPercent) || 0,
    gstRate: itinerary.gstRate != null ? Number(itinerary.gstRate) : 5,
    gstMode: itinerary.gstMode || 'exclusive',
    flightsIncluded: Boolean(itinerary.flightsIncluded),
    flightsNote: itinerary.flightsNote || '',
    isActive: itinerary.isActive !== false,
    days: (itinerary.days || []).map((d) => ({
      ...d,
      services: (d.services || []).map(mapServiceFromApi),
    })),
    hotels: (itinerary.hotels || []).map((h) => ({
      ...h,
      roomConfigs: h.roomConfigs || [],
    })),
    transports: (itinerary.transports || []).map(mapTransportFromApi),
  };
}

const normalizeEditorGraph = (raw) => {
  if (!raw || typeof raw !== 'object') return raw;
  const next = { ...raw };
  if (Array.isArray(next.days)) {
    next.days = next.days.map((d) => ({
      ...d,
      services: (d.services || []).map(mapServiceFromApi),
    }));
  }
  if (Array.isArray(next.transports)) {
    next.transports = next.transports.map(mapTransportFromApi);
  }
  if (Array.isArray(next.hotels)) {
    next.hotels = next.hotels.map((h) => ({
      ...h,
      roomConfigs: h.roomConfigs || [],
    }));
  }
  return next;
};

/** Map legacy AI / itinerary form daysRequests[] → daywise days[] + services. */
export function legacyDaysRequestsToDaywise(daysRequests) {
  if (!Array.isArray(daysRequests)) return [];
  return daysRequests.map((d, i) => {
    const services = [];
    (d.sightSeeings || []).forEach((s) => {
      if (s?.sightSeeingName || s?.sightSeeingId) {
        services.push({
          title: s.sightSeeingName || `Sightseeing ${s.sightSeeingId}`,
          kind: 'Sightseeing',
          rateIndian: 0,
          rateForeign: 0,
          childRateIndian: 0,
          childRateForeign: 0,
          nationalityMode: 'separate',
          enabled: true,
        });
      }
    });
    (d.activities || []).forEach((a) => {
      if (a?.activities) {
        services.push({
          title: a.activities,
          kind: 'Activity',
          rateIndian: 0,
          rateForeign: 0,
          childRateIndian: 0,
          childRateForeign: 0,
          nationalityMode: 'separate',
          enabled: true,
        });
      }
    });
    if (d.lunch === 'Yes' || d.lunch) {
      services.push({
        title: typeof d.lunch === 'string' && d.lunch !== 'Yes' ? d.lunch : 'Lunch',
        kind: 'Meal',
        rateIndian: Number(d.lunchCost) || 0,
        rateForeign: Number(d.lunchCost) || 0,
        childRateIndian: 0,
        childRateForeign: 0,
        nationalityMode: 'shared',
        enabled: true,
      });
    }
    if (d.dinner === 'Yes' || d.dinner) {
      services.push({
        title: typeof d.dinner === 'string' && d.dinner !== 'Yes' ? d.dinner : 'Dinner',
        kind: 'Meal',
        rateIndian: Number(d.dinnerCost) || 0,
        rateForeign: Number(d.dinnerCost) || 0,
        childRateIndian: 0,
        childRateForeign: 0,
        nationalityMode: 'shared',
        enabled: true,
      });
    }
    return {
      dayNumber: i + 1,
      title: d.dayBriefing || `Day ${i + 1}`,
      city: d.accommodationType || '',
      description: d.dayDescription || '',
      legId: i > 0 ? `leg${i}` : '',
      overnightCity: d.accommodationType || '',
      services,
    };
  });
}

function sanitizeDaywiseJsonText(text) {
  let s = String(text ?? '').trim();
  const fenced = s.match(/^```(?:json)?\s*([\s\S]*?)```\s*$/im);
  if (fenced) {
    s = fenced[1].trim();
  } else {
    const start = s.indexOf('{');
    const end = s.lastIndexOf('}');
    if (start >= 0 && end > start) {
      s = s.slice(start, end + 1);
    }
  }
  s = s.replace(/[\u201c\u201d]/g, '"').replace(/[\u2018\u2019]/g, "'");
  s = s.replace(/,\s*,+/g, ',');
  s = s.replace(/,\s*([}\]])/g, '$1');
  return s;
}

function formatDaywiseJsonParseError(rawText, err) {
  const base = err?.message || 'Invalid JSON';
  const lineMatch = base.match(/line (\d+) column (\d+)/i);
  if (!lineMatch) return base;
  const lines = String(rawText ?? '').split('\n');
  const ln = Number(lineMatch[1]);
  const snippet = (lines[ln - 1] || '').trim();
  let tip =
    'Check for duplicate commas (,,), trailing commas, or text outside the JSON object.';
  if (/,,\s*$/.test(snippet) || /,\s*,/.test(snippet)) {
    tip = 'Remove the extra comma on this line (,, → ,).';
  }
  return `${base}\n\nLine ${ln}: ${snippet || '(empty)'}\n${tip}`;
}

/** Parse pasted / AI text into a day-wise itinerary object (tolerates common paste glitches). */
export function parseDaywiseJsonText(text) {
  const sanitized = sanitizeDaywiseJsonText(text);
  try {
    return JSON.parse(sanitized);
  } catch (err) {
    throw new Error(formatDaywiseJsonParseError(sanitized, err));
  }
}

/** Accept AI Grok JSON or editor state; return DaywiseItineraryRequest-shaped object. */
export function normalizeDaywiseImport(raw) {
  if (!raw || typeof raw !== 'object') {
    throw new Error('Invalid JSON');
  }
  if (raw.daysRequests && !raw.days?.length) {
    raw = {
      ...raw,
      days: legacyDaysRequestsToDaywise(raw.daysRequests),
      totalDays: raw.totalDays || raw.day || raw.daysRequests.length,
      totalNights:
        raw.totalNights != null
          ? raw.totalNights
          : Math.max(0, (raw.daysRequests.length || 1) - 1),
    };
  }
  if (!Array.isArray(raw.days) || raw.days.length === 0) {
    throw new Error('JSON must include days[] (daywise schema), not only daysRequests.');
  }
  return normalizeEditorGraph(raw);
}

export function buildDaywiseCreatePayload(data) {
  const normalized = normalizeDaywiseImport({ ...data });
  const payload = stripIds(normalized);
  payload.holidayId = payload.holidayId ? Number(payload.holidayId) : null;
  payload.totalDays = Number(payload.totalDays) || payload.days.length;
  payload.totalNights =
    payload.totalNights != null
      ? Number(payload.totalNights)
      : Math.max(0, payload.totalDays - 1);
  payload.hotels = (payload.hotels || []).map((h) => ({
    ...h,
    roomConfigs: h.roomConfigs || [],
  }));
  payload.days = (payload.days || []).map((d) => ({
    ...d,
    services: (d.services || []).map(mapServiceForApi),
  }));
  payload.transports = (payload.transports || []).map(mapTransportForApi);
  delete payload.daysRequests;
  return payload;
}
